/**
 * Client-Side Rate Limiting Utility
 * 
 * Tracks and enforces rate limits for authentication operations
 * using localStorage to persist across page reloads.
 */

const RATE_LIMIT_CONFIG = {
  signups: {
    maxAttempts: 3,
    windowMs: 60 * 60 * 1000, // 1 hour
    key: 'auth_signup_attempts'
  },
  passwordResets: {
    maxAttempts: 3,
    windowMs: 60 * 60 * 1000, // 1 hour
    key: 'auth_password_reset_attempts'
  },
  emailVerificationResends: {
    maxAttempts: 3,
    windowMs: 60 * 60 * 1000, // 1 hour
    key: 'auth_email_verification_resends'
  }
};

/**
 * Check if an action is allowed based on rate limiting
 * @param {string} type - Type of action ('signups', 'passwordResets', 'emailVerificationResends')
 * @returns {Object} - { allowed: boolean, timeLeft?: number, attemptsRemaining?: number, resetTime?: Date }
 */
export const checkRateLimit = (type) => {
  const config = RATE_LIMIT_CONFIG[type];
  if (!config) {
    console.warn(`Unknown rate limit type: ${type}`);
    return { allowed: true };
  }

  try {
    const stored = localStorage.getItem(config.key);
    const attempts = stored ? JSON.parse(stored) : [];
    const now = Date.now();

    // Filter attempts within the time window
    const recentAttempts = attempts.filter(
      timestamp => now - timestamp < config.windowMs
    );

    if (recentAttempts.length >= config.maxAttempts) {
      // Calculate when the oldest attempt will expire
      const oldestAttempt = recentAttempts[0];
      const resetTime = oldestAttempt + config.windowMs;
      const timeLeft = Math.ceil((resetTime - now) / 1000 / 60); // minutes

      return {
        allowed: false,
        timeLeft,
        resetTime: new Date(resetTime),
        attemptsRemaining: 0
      };
    }

    return {
      allowed: true,
      attemptsRemaining: config.maxAttempts - recentAttempts.length
    };
  } catch (error) {
    console.error('Error checking rate limit:', error);
    // On error, allow the action (fail open)
    return { allowed: true };
  }
};

/**
 * Record an attempt for rate limiting
 * @param {string} type - Type of action
 */
export const recordAttempt = (type) => {
  const config = RATE_LIMIT_CONFIG[type];
  if (!config) {
    console.warn(`Unknown rate limit type: ${type}`);
    return;
  }

  try {
    const stored = localStorage.getItem(config.key);
    const attempts = stored ? JSON.parse(stored) : [];
    attempts.push(Date.now());

    // Keep only the last 20 attempts to prevent storage bloat
    const recentAttempts = attempts.slice(-20);
    localStorage.setItem(config.key, JSON.stringify(recentAttempts));
  } catch (error) {
    console.error('Error recording rate limit attempt:', error);
    // Silently fail - don't block the user
  }
};

/**
 * Clear rate limit attempts for a specific type
 * @param {string} type - Type of action
 */
export const clearRateLimit = (type) => {
  const config = RATE_LIMIT_CONFIG[type];
  if (!config) return;

  try {
    localStorage.removeItem(config.key);
  } catch (error) {
    console.error('Error clearing rate limit:', error);
  }
};

/**
 * Get formatted time remaining message
 * @param {number} timeLeft - Time left in minutes
 * @returns {string} - Formatted message
 */
export const getTimeLeftMessage = (timeLeft) => {
  if (timeLeft < 1) {
    return 'less than a minute';
  } else if (timeLeft === 1) {
    return '1 minute';
  } else if (timeLeft < 60) {
    return `${timeLeft} minutes`;
  } else {
    const hours = Math.floor(timeLeft / 60);
    const minutes = timeLeft % 60;
    if (minutes === 0) {
      return hours === 1 ? '1 hour' : `${hours} hours`;
    }
    return `${hours} hour${hours > 1 ? 's' : ''} and ${minutes} minute${minutes > 1 ? 's' : ''}`;
  }
};

